/**
 * One bounded track. Three uses, one shape.
 *
 * A range bar (52-week low to high), a daylight bar (sunrise to sunset) and a
 * meter (68% occupancy) are the same object drawn three times, and drawing
 * them three times is what produced three different bar heights, three radii
 * and three ideas about what an empty bar means. Here there is one component,
 * one geometry and one answer.
 *
 * Two rules are absolute. `label` and `value` are REQUIRED, because a bare bar
 * is a picture of a number nobody can read — the bar shows the relation, the
 * figure shows the level, and a track that ships without the figure has thrown
 * away the only part a reader can quote. And an empty track has to declare
 * what it means: `Tick` says the value was MEASURED and it is zero, so it
 * paints a baseline tick; `Absent` says nothing was measured, so it paints no
 * track at all. An empty bar with no policy reads as a measured zero, and that
 * is a fabricated reading.
 *
 * The fill grows from its leading edge exactly once, through the shared
 * `cresultcard__fill` class, so a bar reads as a measurement being taken rather
 * than as decoration that happened to be there.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
// `cresultcard__fill` carries the bar's draw and its logical growth origin, so
// this file does not render correctly without the stylesheet on the page.
import "./Cresultcard.css";
import {
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT_FAINT,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
  CRC_WELL_BACKGROUND_COLOR,
  CRC_WELL_SHADOW,
} from "./cresultcard.tokens";
import {
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTone,
  CresultcardTrackGradientSpan,
  CresultcardTrackZeroPolicy,
} from "./cresultcard.types";

const TRACK_HEIGHT = 7;
/**
 * The bed's intrinsic contribution. `flex-basis: 0` with no floor measures zero
 * in an intrinsically-sized zone — a shrink-to-fit column, a grid track sized to
 * `max-content` — so the bar vanished and only the caption survived. A basis is
 * a floor and not a width: in a definite-width holder the single growing item
 * still takes every remaining pixel, so a track that fits today is unchanged.
 */
const TRACK_BED_BASIS = 64;
/** A measured zero still has to be visible, so the tick outgrows the bed. */
const TICK_WIDTH = 2;
const TICK_OVERHANG = 2;
const GLYPH_SIZE = 13;
const GLYPH_STROKE = 1.6;
const ROW_GAP = 8;
const PERCENT = 100;
const RATIO_PRECISION = 3;

export interface CresultcardTrackDomain {
  readonly min: number;
  readonly max: number;
}

/** A band when `end` is given, a meter from the domain's floor when it is not. */
export interface CresultcardTrackSegment {
  readonly start: number;
  readonly end?: number;
}

/** The two ends of the scale, as glyphs: a moon and a sun, a down and an up. */
export interface CresultcardTrackEndGlyphs {
  readonly start: TablerIcon;
  readonly end: TablerIcon;
}

/** Both stops are tones, never hues: a track cannot invent a colour. */
export interface CresultcardTrackGradient {
  readonly from: CresultcardTone;
  readonly to: CresultcardTone;
  /** What the ramp spans. Defaults to the drawn segment, as it always did. */
  readonly span?: CresultcardTrackGradientSpan;
}

interface CresultcardTrackProps {
  readonly domain: CresultcardTrackDomain;
  /** Absent means "not measured", which the zero policy then interprets. */
  readonly segment?: CresultcardTrackSegment;
  /** Required: a track without a name is a bar nobody can attribute. */
  readonly label: string;
  /** Required: the bar carries the relation, the figure carries the level. */
  readonly value: ReactNode;
  readonly tone?: CresultcardTone;
  readonly endGlyphs?: CresultcardTrackEndGlyphs;
  readonly gradient?: CresultcardTrackGradient;
  readonly zeroPolicy: CresultcardTrackZeroPolicy;
}

interface TrackExtent {
  readonly start: number;
  readonly size: number;
}

interface TrackExtentInput {
  readonly domain: CresultcardTrackDomain;
  readonly segment?: CresultcardTrackSegment;
}

function trackExtent({ domain, segment }: TrackExtentInput): TrackExtent {
  const span = domain.max - domain.min;
  if (segment == null || span <= 0) {
    return { start: 0, size: 0 };
  }
  const ratioOf = (value: number): number => {
    const clamped = Math.min(Math.max(value, domain.min), domain.max);
    return (clamped - domain.min) / span;
  };
  // A single value is a meter: it runs from the domain's floor to the reading.
  // A pair is a band: it runs between the two readings.
  const startRatio = segment.end == null ? 0 : ratioOf(segment.start);
  const endRatio = ratioOf(segment.end ?? segment.start);
  return { start: startRatio, size: Math.max(endRatio - startRatio, 0) };
}

function percentOf(ratio: number): string {
  return `${(ratio * PERCENT).toFixed(RATIO_PRECISION)}%`;
}

interface FillGradientStyleInput {
  readonly gradient?: CresultcardTrackGradient;
  readonly extent: TrackExtent;
}

/**
 * The fill's gradient layer.
 *
 * The fill element IS the segment, so a gradient painted on it runs from the
 * segment's own start to its own end — which is why a set of tracks sharing one
 * domain could not share one ramp: each bar restarted the ramp at its own left
 * edge. `Domain` sizes the image to the whole bed and slides it so the ramp's
 * origin sits at the domain's origin, then the segment shows the slice of it
 * that it actually covers.
 */
function fillGradientStyle({
  gradient,
  extent,
}: FillGradientStyleInput): CSSProperties {
  if (gradient == null) {
    return {};
  }
  const image = `linear-gradient(90deg, ${CRC_TONE_COLOR[gradient.from]}, ${CRC_TONE_COLOR[gradient.to]})`;
  if (gradient.span !== CresultcardTrackGradientSpan.Domain) {
    return { backgroundImage: image };
  }
  // The image is the bed's width; the fill is `extent.size` of it. A background
  // position of P places the image so that (fillWidth − imageWidth) × P equals
  // the image origin's offset inside the fill — solving for the domain origin
  // sitting at the bed's own start gives start / (1 − size).
  const remaining = 1 - extent.size;
  const position =
    remaining <= 0 ? 0 : (extent.start / remaining) * PERCENT;
  return {
    backgroundImage: image,
    backgroundSize: `${(PERCENT / extent.size).toFixed(RATIO_PRECISION)}% 100%`,
    backgroundPosition: `${position.toFixed(RATIO_PRECISION)}% 0%`,
    backgroundRepeat: "no-repeat",
  };
}

export function CresultcardTrack({
  domain,
  segment,
  label,
  value,
  tone = CresultcardTone.Positive,
  endGlyphs,
  gradient,
  zeroPolicy,
}: CresultcardTrackProps): ReactElement {
  const extent = trackExtent({ domain, segment });
  const color = CRC_TONE_COLOR[tone];
  const measuredZero = extent.size === 0;
  const tickPolicy = zeroPolicy === CresultcardTrackZeroPolicy.Tick;
  // An absent value paints no track: an empty bed reads as a measured zero,
  // and a measured zero is a claim we have not earned.
  const showTrack = !measuredZero || tickPolicy;
  const StartGlyph = endGlyphs?.start;
  const EndGlyph = endGlyphs?.end;

  return (
    <div
      data-el="Cresultcard/Track/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: CRC_STACK_GAP[CresultcardStackDensity.Tight],
        minWidth: 0,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: ROW_GAP,
          minWidth: 0,
        }}
      >
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            minWidth: 0,
            textAlign: "start",
          }}
        >
          {label}
        </span>
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Body],
            ...CRC_NUMERIC_STYLE,
            flexShrink: 0,
            color,
            textAlign: "end",
          }}
        >
          {value}
        </span>
      </div>

      {showTrack && (
        <div
          aria-hidden
          style={{
            display: "flex",
            alignItems: "center",
            gap: ROW_GAP,
            minWidth: 0,
          }}
        >
          {StartGlyph != null && (
            <span
              style={{
                display: "inline-flex",
                flexShrink: 0,
                color: CRC_TEXT_FAINT,
              }}
            >
              <StartGlyph size={GLYPH_SIZE} stroke={GLYPH_STROKE} />
            </span>
          )}
          <span
            data-el="Cresultcard/Track/Scale"
            style={{
              position: "relative",
              display: "block",
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: TRACK_BED_BASIS,
              minWidth: 0,
              height: TRACK_HEIGHT,
              borderRadius: TRACK_HEIGHT,
              backgroundColor: CRC_WELL_BACKGROUND_COLOR,
              boxShadow: CRC_WELL_SHADOW,
            }}
          >
            {measuredZero ? (
              <span
                data-crc-zero="true"
                style={{
                  position: "absolute",
                  insetBlock: -TICK_OVERHANG,
                  insetInlineStart: percentOf(extent.start),
                  width: TICK_WIDTH,
                  borderRadius: TICK_WIDTH,
                  backgroundColor: color,
                }}
              />
            ) : (
              <span
                className="cresultcard__fill"
                style={{
                  position: "absolute",
                  insetBlock: 0,
                  insetInlineStart: percentOf(extent.start),
                  width: percentOf(extent.size),
                  borderRadius: TRACK_HEIGHT,
                  backgroundColor: color,
                  ...fillGradientStyle({ extent, gradient }),
                }}
              />
            )}
          </span>
          {EndGlyph != null && (
            <span
              style={{
                display: "inline-flex",
                flexShrink: 0,
                color: CRC_TEXT_FAINT,
              }}
            >
              <EndGlyph size={GLYPH_SIZE} stroke={GLYPH_STROKE} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
